import { jsPDF } from "jspdf";
import autoTable, { type RowInput } from "jspdf-autotable";
import type { FicheConfig } from "@/types/fiche";
import type { Cycle, SchoolClass } from "@/types";
import { formatMoney } from "./format";

const fg = (n: number) => formatMoney(n);

/**
 * Génère et télécharge la « Fiche de renseignements » en PDF — sur une seule page.
 * Les couleurs sont fixes (RGB) : un PDF ne lit pas les variables de thème CSS.
 * `showDueDates` permet d'afficher ou de masquer les dates d'échéance des tranches.
 */
export function exportFichePdf(
  config: FicheConfig,
  classes: SchoolClass[],
  cycles: Cycle[],
  showDueDates = true,
): void {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 36;
  let y = 42;

  // En-tête
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(28, 28, 26);
  doc.text(config.header.group.toUpperCase(), W / 2, y, { align: "center" });
  y += 13;
  doc.setFontSize(8);
  doc.setTextColor(70, 70, 66);
  doc.text(config.header.subtitle.toUpperCase(), W / 2, y, { align: "center" });
  y += 11;
  doc.setTextColor(29, 95, 208);
  doc.text(config.header.cycles.toUpperCase(), W / 2, y, { align: "center" });
  y += 10;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(100, 100, 96);
  doc.text(`CONTACTS : ${config.header.contacts}`, W / 2, y, { align: "center" });
  y += 10;
  doc.setFont("helvetica", "bold");
  doc.setTextColor(60, 60, 56);
  doc.text(`ANNÉE SCOLAIRE : ${config.header.schoolYear}`, W / 2, y, { align: "center" });
  y += 12;
  doc.setDrawColor(40, 40, 38);
  doc.setLineWidth(1);
  doc.line(M, y, W - M, y);
  y += 16;

  doc.setFontSize(11);
  doc.setTextColor(28, 28, 26);
  doc.text("FICHE DE RENSEIGNEMENTS", W / 2, y, { align: "center" });
  y += 18;

  const primaryCycle = cycles.find((c) => c.code === "PRIMAIRE");
  const secondaryCycle = cycles.find((c) => c.code === "SECONDAIRE");
  const primaryClasses = primaryCycle ? classes.filter((c) => c.cycleId === primaryCycle.id) : [];
  const secondaryClasses = secondaryCycle ? classes.filter((c) => c.cycleId === secondaryCycle.id) : [];
  const maxTranches = Math.max(
    3,
    ...[...primaryClasses, ...secondaryClasses].map(
      (c) => config.classFiches.find((f) => f.classId === c.id)?.tranches.length ?? 0,
    ),
  );

  // Dossiers
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(28, 28, 26);
  doc.text("DOSSIERS À FOURNIR", M, y);
  y += 11;

  const unionDossiers = (list: SchoolClass[]) => {
    const ids = new Set<string>();
    list.forEach((c) => {
      const f = config.classFiches.find((x) => x.classId === c.id);
      f?.dossierIds.forEach((id) => ids.add(id));
    });
    return config.dossiers
      .filter((d) => ids.has(d.id))
      .map((d) => `- ${d.label}`)
      .join("   ");
  };

  const dossColW = (W - M * 2 - 12) / 2;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(55, 55, 52);
  doc.text("Maternelle & Primaire", M, y);
  doc.text("Collège & Lycée", M + dossColW + 12, y);
  y += 10;
  doc.setFont("helvetica", "normal");
  const leftLines = doc.splitTextToSize(unionDossiers(primaryClasses), dossColW);
  const rightLines = doc.splitTextToSize(unionDossiers(secondaryClasses), dossColW);
  const rowsCount = Math.max(leftLines.length, rightLines.length);
  doc.text(leftLines, M, y);
  doc.text(rightLines, M + dossColW + 12, y);
  y += rowsCount * 10 + 4;

  // Frais d'inscription / réinscription (uniques)
  doc.setDrawColor(60, 60, 56);
  doc.setLineWidth(0.5);
  doc.setFillColor(240, 240, 236);
  doc.rect(M, y, W - M * 2, 15, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(28, 28, 26);
  doc.text(
    `FRAIS D'INSCRIPTION / RÉINSCRIPTION :  Inscription ${fg(config.enrollmentFees.inscription)}   ·   Réinscription ${fg(
      config.enrollmentFees.reinscription,
    )}   (montants uniques, toutes classes confondues)`,
    M + 6,
    y + 10,
  );
  y += 24;

  // Articles
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(28, 28, 26);
  doc.text("ARTICLES VENDUS À L'ÉCOLE", M, y);
  y += 11;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(55, 55, 52);
  const articleLines = doc.splitTextToSize(
    config.articles.map((a) => `${a.label} : ${fg(a.amount)}`).join("     |     "),
    W - M * 2,
  );
  doc.text(articleLines, M, y);
  y += articleLines.length * 10 + 4;

  // Frais d'écolage — un seul tableau
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(28, 28, 26);
  doc.text("FRAIS D'ÉCOLAGE ET MODE DE PAIEMENT", M, y);

  const head = [
    "Classe",
    ...Array.from({ length: maxTranches }).map((_, i) => `Tranche ${i + 1}`),
    "Annuelle",
  ];

  const buildRows = (list: SchoolClass[]) =>
    list
      .map((cls) => ({ cls, fiche: config.classFiches.find((f) => f.classId === cls.id) }))
      .filter((r) => r.fiche)
      .map(({ cls, fiche }) => {
        const total = fiche!.tranches.reduce((s, t) => s + t.amount, 0);
        const trancheCells = Array.from({ length: maxTranches }).map((_, i) => {
          const tr = fiche!.tranches[i];
          if (!tr) return "—";
          return showDueDates && tr.dueDate ? `${fg(tr.amount)}\n${tr.dueDate}` : fg(tr.amount);
        });
        return [cls.name, ...trancheCells, fg(total)];
      });

  /** Ligne de titre de groupe : une seule cellule sur toute la largeur. */
  const groupRow = (title: string): RowInput => [
    {
      content: title,
      colSpan: maxTranches + 2,
      styles: {
        fillColor: [236, 238, 233] as [number, number, number],
        textColor: [45, 50, 45] as [number, number, number],
        fontStyle: "bold" as const,
        fontSize: 7,
      },
    },
  ];

  const body: RowInput[] = [
    groupRow("A. MATERNELLE & PRIMAIRE"),
    ...buildRows(primaryClasses),
    groupRow("B. COLLÈGE & LYCÉE"),
    ...buildRows(secondaryClasses),
  ];

  autoTable(doc, {
    theme: "grid",
    head: [head],
    body,
    startY: y + 6,
    margin: { left: M, right: M },
    styles: {
      font: "helvetica",
      fontSize: 7,
      cellPadding: 2.5,
      textColor: [55, 55, 52],
      lineColor: [206, 206, 200],
      lineWidth: 0.4,
      valign: "middle",
    },
    headStyles: {
      fillColor: [234, 238, 245],
      textColor: [55, 60, 55],
      fontStyle: "bold",
      fontSize: 7,
    },
    columnStyles: {
      0: { cellWidth: 70, fontStyle: "bold" },
      [maxTranches + 1]: { halign: "right", fontStyle: "bold" },
    },
  });

  let after = (doc as any).lastAutoTable?.finalY ?? y + 6;

  // Recouvrement
  after += 14;
  doc.setFontSize(9);
  doc.setTextColor(28, 28, 26);
  doc.setFont("helvetica", "bold");
  doc.text("RAPPEL : LES PÉRIODES DE RECOUVREMENT", M, after);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(55, 55, 52);
  let py = after + 12;
  config.periods.forEach((p) => {
    doc.text(`${p.label} : ${p.value}`, M, py);
    py += 10;
  });

  // Notices
  py += 4;
  doc.setFont("helvetica", "italic");
  doc.setTextColor(90, 90, 86);
  doc.setFontSize(7);
  config.notices.forEach((n) => {
    const lines = doc.splitTextToSize(`NB : ${n}`, W - M * 2);
    doc.text(lines, M, py);
    py += lines.length * 9 + 3;
  });

  // Pied
  py += 14;
  doc.setFont("helvetica", "bolditalic");
  doc.setTextColor(55, 55, 52);
  doc.setFontSize(8);
  doc.text(config.closing, M, py);
  doc.text(config.signature, W - M, py, { align: "right" });

  const stamp = new Date().toISOString().slice(0, 10);
  doc.save(`fiche-de-renseignements-${config.header.schoolYear}-${stamp}.pdf`);
}